import React, { useEffect } from 'react';
import { gql } from '@apollo/client';
import { capitalizeWord } from '@moda/portal-stanchions';
import MailIcon from '@moda/icons/mail-16';
import CheckmarkIcon from '@moda/icons/checkmark-16';
import { Text, Stack, Divider, Input, Checkbox, Button, Overlay, LoadingBalls } from '@moda/om';
import {
  BackInStockNotificationFormFragment,
  BackInStockNotificationFormInventoryFragment
} from '../../generated/types';
import { tc } from '../../lib/trackingContext';
import { getActiveVariantBy } from '../../lib/getActiveVariantBy';
import { useBackInStockNotificationForm } from '../../hooks/useBackInStockNotificationForm';
import { Image } from '../Image';
import {
  VARIANT_CELL_PRODUCT_PRICE_FRAGMENT,
  VariantCellProductPrice
} from '../Grid/GridCell/VariantCell/VariantCellProductPrice';
import './BackInStockNotificationForm.scss';

export const BACK_IN_STOCK_NOTIFICATON_FORM_INVENTORY_FRAGMENT = gql`
  fragment BackInStockNotificationFormInventoryFragment on Inventory {
    sku
    size
  }
`;

export const BACK_IN_STOCK_NOTIFICATON_FORM_VARIANT_FRAGMENT = gql`
  fragment BackInStockNotificationFormVariantFragment on VariantData {
    color
    customColorName: custom_color_name
    primaryImageUrls: primary_image_urls {
      src: medium
    }
  }
`;

export const BACK_IN_STOCK_NOTIFICATON_FORM_FRAGMENT = gql`
  fragment BackInStockNotificationFormFragment on Variant {
    id
    name
    designerName: designer_name
    masterVariant: master_variants_data {
      ...BackInStockNotificationFormVariantFragment
    }
    otherVariants: other_variants_data {
      ...BackInStockNotificationFormVariantFragment
    }
    ...VariantCellProductPriceFragment
  }
  ${BACK_IN_STOCK_NOTIFICATON_FORM_VARIANT_FRAGMENT}
  ${VARIANT_CELL_PRODUCT_PRICE_FRAGMENT}
`;

type Props = {
  activeVariantId: string;
  variant: BackInStockNotificationFormFragment;
  size?: BackInStockNotificationFormInventoryFragment;
  onClose: () => void;
  place?: string;
};

export const BackInStockNotificationForm: React.FC<Props> = ({
  activeVariantId,
  variant,
  size,
  onClose,
  place
}) => {
  const activeVariant = getActiveVariantBy(variant, 'id', activeVariantId);

  const {
    error,
    loading,
    isSubscribed,
    email,
    setEmail,
    extraSubscriptionsChecked,
    setExtraSubscriptionsChecked,
    handleClick
  } = useBackInStockNotificationForm(activeVariant, size?.sku);

  useEffect(() => {
    tc.track('PDP Back In Stock Notification Request Displayed', {
      addToScopeOnce: { activeVariant },
      addToTrackingEvent: { label: activeVariant.id, sku: size?.sku || '', place: place || '' }
    });
  }, [activeVariant, size, place]);

  return (
    <Stack className="BackInStockNotificationForm" space={4}>
      <Stack
        className="BackInStockNotificationForm__product"
        space={4}
        direction="horizontal"
        alignItems="center"
      >
        <div className="BackInStockNotificationForm__product-image">
          {activeVariant.primaryImageUrls.src && (
            <Image
              src={activeVariant.primaryImageUrls.src}
              alt={variant.name || 'Product'}
              width="180"
              height="320"
            />
          )}
        </div>
        <Stack space={0}>
          <Text treatment="bold1">{variant.designerName}</Text>
          <Text>{variant.name}</Text>
          <Text>Color: {capitalizeWord(activeVariant.customColorName || activeVariant.color)}</Text>
          {size && <Text>Size: {size.size}</Text>}
          <VariantCellProductPrice variant={variant} activeVariantId={activeVariantId} />
        </Stack>
      </Stack>
      {!isSubscribed && (
        <>
          <Stack space={4}>
            <Divider />
            <Text>
              Receive an email notification when this item is available. Enter an email address and
              tap notify me.
            </Text>
            <Input
              name="email"
              placeholder="Email"
              error={error}
              value={email}
              onChange={setEmail}
            />
            <Checkbox
              checked={extraSubscriptionsChecked}
              onChange={({ target: { checked } }) => setExtraSubscriptionsChecked(checked)}
            >
              Put me on the email list for style inspiration & designer announcements.
            </Checkbox>
            <Divider />
          </Stack>
          <Button onClick={handleClick}>
            <Stack space={2} direction="horizontal" alignItems="center">
              <MailIcon />
              <Text color="snow">Notify Me</Text>
            </Stack>
          </Button>
        </>
      )}
      {isSubscribed && (
        <>
          <Stack space={4}>
            <Divider />
            <Stack space={3} direction="horizontal" alignItems="center">
              <Text color="money-good">
                <CheckmarkIcon />
              </Text>
              <Text color="money-good">
                We will email you at{' '}
                <Text treatment="bold1" color="money-good">
                  {email}
                </Text>{' '}
                when this item becomes available again.
              </Text>
            </Stack>
            <Divider />
          </Stack>
          <Button onClick={onClose}>Done</Button>
        </>
      )}
      <Overlay show={loading}>
        <LoadingBalls />
        <Text treatment="eyebrow">Saving</Text>
      </Overlay>
    </Stack>
  );
};
